/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unstable-nested-components */
import * as React from 'react';
import { Box, Button, InputLabel, Stack, Typography } from '@mui/material';
import {
  Configure,
  Hits,
  InstantSearch,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch';
import { useFormikContext } from 'formik';
import CloseIcon from '@mui/icons-material/HighlightOffRounded';
import { getCleanedPhone } from '../../utils/global/helper';
import { applyAlgoliaRestriction } from '../../helpers/global';
import { INDEX, searchClient } from '../../algoliaconfig';
import { GeneralRequest } from '../../interfaces';
import StyledPhoneInput from './PhoneInput';
import 'instantsearch.css/themes/satellite.css';
import { isCustomerEditDisabled } from '../../services/customers';
import TextInput from './TextInput';
import {
  ClientSearchAutofillValues,
  getInputName,
  getValue,
} from '../../utils/form/clientValues';
import useUser from '../../hooks/global/useUser';
import useRestrictedBusinessIds from '../../hooks/global/useRestrictedBusinessIds';
import useBusinessWebRedesign from '../../hooks/businesses/useBusinessWebRedesign';
import { SelectedAppointment } from '../../types/appointments';
import useCustomerScheduleBlockWarning from '../../hooks/customers/useCustomerScheduleBlockWarning';
import CustomerScheduleBlockAlert from '../customers/CustomerScheduleBlockAlert';
import ClientCard from './ClientCard';
import ClientFields from './ClientFields';
import ClientSearchResults from './ClientSearchResults';
import useApplyClientHit from '../../hooks/customers/useApplyClientHit';
import { ClientDisplayAppointment } from '../../helpers/customers';

function Hit({
  hit,
  setShowHits,
  namePrefix = [],
}: {
  hit: any;
  setShowHits: (show: boolean) => void;
  namePrefix?: string[];
}) {
  const applyHit = useApplyClientHit(namePrefix);

  const handleClick = () => {
    setShowHits(false); // Hide hits after click
    applyHit(hit);
  };

  return (
    <Box width="100%" onClick={handleClick}>
      <Typography variant="h5">{hit.fullName}</Typography>
      <Typography variant="body2">
        {getCleanedPhone(hit.phoneNumber?.toString() ?? '')}, {hit.email}
      </Typography>
    </Box>
  );
}

function AddClientBoundary({ children, handleCreateNewClient }: any) {
  const { indexUiState } = useInstantSearch();

  return (
    <>
      {children}
      <Box
        onClick={() => handleCreateNewClient(indexUiState.query || '')}
        className="create-client-hit">
        <Typography
          variant="body2"
          style={{
            color: 'var(--darkGrey)',
            paddingTop: 14,
            paddingBottom: 14,
            paddingLeft: 28,
          }}>
          Create new client &quot;
          <span style={{ fontWeight: 600 }}>{indexUiState.query}</span>&quot;
        </Typography>
      </Box>
    </>
  );
}

function CustomSearchBox({
  setShowHits,
  showHits,
  onReset,
}: {
  showHits: boolean;
  setShowHits: (show: boolean) => void;
  onReset: () => void;
}) {
  const { query, refine, clear } = useSearchBox();

  const handleReset = () => {
    clear();
    refine('');
    setShowHits(false); // Hide hits on reset
    onReset();
  };

  return (
    <Box
      position="relative"
      sx={{
        display: 'flex',
        flex: 1,
      }}>
      <input
        type="text"
        value={query}
        onChange={e => refine(e.target.value)}
        style={{
          flex: 1,
          flexGrow: 1,
        }}
        placeholder="Select or create client"
        onFocus={() => setShowHits(true)} // Show hits on focus
        autoComplete="off"
      />
      {(!!query || showHits) && (
        <Stack
          position="absolute"
          right={theme => theme.spacing(0.5)}
          height="100%"
          display="flex"
          justifyContent="center"
          zIndex={6}>
          <Button
            type="button"
            onClick={handleReset}
            sx={{ minWidth: 0, padding: 1 }}>
            <CloseIcon
              style={{
                color: 'var(--darkGrey)',
                fontSize: 20,
              }}
            />
          </Button>
        </Stack>
      )}
    </Box>
  );
}

interface Props {
  selectedRequest?: SelectedAppointment | GeneralRequest;
  selectedAppointment?: SelectedAppointment | null;
  allowCustomerWithoutAppointment?: boolean;
  /** null renders the section without a heading. */
  title?: string | null;
  hideBirthday?: boolean;
  namePrefix?: string[];
  disableCustomerEdit?: boolean;
  showScheduleBlockWarning?: boolean;
}

export default function ClientSearchAutofill({
  selectedRequest,
  selectedAppointment,
  allowCustomerWithoutAppointment,
  title = 'Client Information',
  hideBirthday = false,
  namePrefix = [],
  disableCustomerEdit: disableCustomerEditProp = false,
  showScheduleBlockWarning = false,
}: Props) {
  const { user: loadedUser } = useUser();
  const restrictedBusinessIds = useRestrictedBusinessIds();
  const { active: redesignActive } = useBusinessWebRedesign();
  const { values, setFieldValue, validateForm } =
    useFormikContext<ClientSearchAutofillValues>();
  const [showHits, setShowHits] = React.useState(false);
  const [creatingNewClient, setCreatingNewClient] = React.useState(false);

  const customerIdValue = getValue(values, namePrefix, 'customerId');
  const firstNameValue = getValue(values, namePrefix, 'firstName');

  const { warning: scheduleBlockWarning } = useCustomerScheduleBlockWarning({
    customerId: showScheduleBlockWarning ? customerIdValue : null,
    businessId: loadedUser?.business.id,
    employeeId: getValue(values, namePrefix, 'employeeId') || null,
  });

  const customer = (selectedRequest ?? selectedAppointment)?.customer;
  const customerWithoutAppt =
    !!customerIdValue && allowCustomerWithoutAppointment && !customer
      ? {
          role: getValue(values, namePrefix, 'role') || 'CUSTOMER',
          phoneNumber: getValue(values, namePrefix, 'phoneNumber'),
        }
      : null;

  const showCustomerInputs =
    creatingNewClient ||
    selectedAppointment ||
    selectedRequest ||
    customerWithoutAppt;
  const customerGuestId = getValue(values, namePrefix, 'customerGuestId');
  const disableCustomerEdit =
    !customerGuestId &&
    (!!customer || !!customerWithoutAppt) &&
    (disableCustomerEditProp ||
      isCustomerEditDisabled((customer || customerWithoutAppt)!));

  const algoliaFilters = React.useMemo(() => {
    const shopFilterString = `businessId:${loadedUser?.business.id}`;
    const artistFilterString = `employeeId:${loadedUser?.userId}`;
    const restrict = (filter: string) =>
      applyAlgoliaRestriction(filter, restrictedBusinessIds);

    if (!loadedUser?.canViewAllShopCustomers) {
      return restrict(artistFilterString);
    }
    return restrict(`${shopFilterString} OR ${artistFilterString}`);
  }, [
    loadedUser?.business.id,
    loadedUser?.canViewAllShopCustomers,
    restrictedBusinessIds,
    loadedUser?.userId,
  ]);

  // Function to handle the creation of a new client
  const handleCreateNewClient = async (query: string) => {
    const names = query.split(' ');
    setShowHits(false); // Hide hits list
    setCreatingNewClient(true);
    await setFieldValue(
      getInputName(namePrefix, 'firstName'),
      names[0] || '',
      false,
    );
    await setFieldValue(
      getInputName(namePrefix, 'lastName'),
      names.length > 1 ? names.slice(1).join(' ') : '',
      false,
    );
    await setFieldValue(getInputName(namePrefix, 'email'), '', false);
    await setFieldValue(getInputName(namePrefix, 'phoneNumber'), '+1', false);
    await setFieldValue(getInputName(namePrefix, 'dob'), '', false);
    await validateForm();
  };

  const onClearCustomer = async () => {
    // Skip per-field validation while batch-clearing: concurrent async
    // validations race and can leave stale errors/isValid behind.
    await setFieldValue(getInputName(namePrefix, 'customerId'), '', false);
    await setFieldValue(getInputName(namePrefix, 'firstName'), '', false);
    await setFieldValue(getInputName(namePrefix, 'lastName'), '', false);
    await setFieldValue(getInputName(namePrefix, 'email'), '', false);
    await setFieldValue(getInputName(namePrefix, 'phoneNumber'), '', false);
    await setFieldValue(getInputName(namePrefix, 'dob'), '', false);
    await setFieldValue(getInputName(namePrefix, 'role'), '', false);
    setCreatingNewClient(false);
    await validateForm();
  };

  if (redesignActive) {
    // Keyed on customerId, not a typed field: clearing first name while
    // editing must not collapse the card.
    const selectedClient =
      !creatingNewClient &&
      (!!customerIdValue || !!selectedAppointment || !!selectedRequest);
    // General requests carry no booking, so the card falls back to contact
    // details for its meta line.
    const appointmentContext = (selectedAppointment ??
      selectedRequest) as ClientDisplayAppointment | null;

    return (
      <div className="input-group">
        {!!title && <Typography variant="h2">{title}</Typography>}
        <Stack gap={2} width="100%">
          {selectedClient ? (
            <ClientCard
              namePrefix={namePrefix}
              appointment={appointmentContext}
              disableCustomerEdit={disableCustomerEdit}
              hideBirthday={hideBirthday}
              canRemove={!customer}
              onRemove={onClearCustomer}
            />
          ) : (
            <InstantSearch
              searchClient={searchClient}
              indexName={INDEX}
              insights>
              <Configure filters={algoliaFilters} distinct />
              <Stack gap={2}>
                <ClientSearchResults
                  namePrefix={namePrefix}
                  onCreateNewClient={handleCreateNewClient}
                  onReset={onClearCustomer}
                  onSelectClient={() => setCreatingNewClient(false)}
                />
                {creatingNewClient && (
                  <ClientFields
                    namePrefix={namePrefix}
                    disabled={disableCustomerEdit}
                    disablePhone={!!customerGuestId}
                    hideBirthday={hideBirthday}
                  />
                )}
              </Stack>
            </InstantSearch>
          )}
          {disableCustomerEdit && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              This customer&apos;s information cannot be edited
            </Typography>
          )}
        </Stack>
      </div>
    );
  }

  return (
    <div className="input-group client-autofill">
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="h2">{title}</Typography>
        {showCustomerInputs && !!firstNameValue && !customer && (
          <Button
            sx={{
              minWidth: 0,
              p: 0.5,
            }}
            onClick={onClearCustomer}>
            <CloseIcon
              sx={{
                color: 'text.secondary',
                fontSize: 20,
              }}
            />
          </Button>
        )}
      </Stack>
      <CustomerScheduleBlockAlert warning={scheduleBlockWarning} mb={0} />
      <div className="inputs">
        {!showCustomerInputs && (
          <div className="input-row">
            <div className="input">
              <InputLabel htmlFor="name">
                <Typography variant="h5">Name *</Typography>
              </InputLabel>
              <InstantSearch
                searchClient={searchClient}
                indexName={INDEX}
                insights>
                <Configure filters={algoliaFilters} distinct />
                <div style={{ flex: 1, position: 'relative' }}>
                  <CustomSearchBox
                    showHits={showHits}
                    setShowHits={setShowHits}
                    onReset={onClearCustomer}
                  />
                  {showHits && (
                    <AddClientBoundary
                      handleCreateNewClient={handleCreateNewClient}>
                      <Hits
                        hitComponent={hit => (
                          <Hit
                            hit={hit.hit}
                            setShowHits={setShowHits}
                            namePrefix={namePrefix}
                          />
                        )}
                      />
                    </AddClientBoundary>
                  )}
                </div>
              </InstantSearch>
            </div>
          </div>
        )}
        {showCustomerInputs && (
          <div className="input-row">
            <TextInput
              label="First Name *"
              name={getInputName(namePrefix, 'firstName')}
              disabled={disableCustomerEdit}
              placeholder="John"
              value={getValue(values, namePrefix, 'firstName')}
              onChange={e => {
                setFieldValue(
                  getInputName(namePrefix, 'firstName'),
                  e.target.value,
                );
              }}
            />
            <TextInput
              label="Last Name"
              name={getInputName(namePrefix, 'lastName')}
              disabled={disableCustomerEdit}
              placeholder="Doe"
              value={getValue(values, namePrefix, 'lastName')}
              onChange={e => {
                setFieldValue(
                  getInputName(namePrefix, 'lastName'),
                  e.target.value,
                );
              }}
            />
          </div>
        )}
        <div className="input-row">
          <TextInput
            label="Email"
            name={getInputName(namePrefix, 'email')}
            type="email"
            placeholder="Email"
            disabled={
              (!creatingNewClient && !firstNameValue) || disableCustomerEdit
            }
            value={getValue(values, namePrefix, 'email')}
            onChange={e => {
              setFieldValue(getInputName(namePrefix, 'email'), e.target.value);
            }}
          />

          <StyledPhoneInput
            placeholder="Phone Number"
            name={getInputName(namePrefix, 'phoneNumber')}
            label="Phone *"
            disabled={
              !!customerGuestId ||
              (!creatingNewClient && !firstNameValue) ||
              disableCustomerEdit
            }
          />
        </div>
        {(creatingNewClient || selectedAppointment) && !hideBirthday && (
          <div className="input-row">
            <TextInput
              label="Birthday"
              name={getInputName(namePrefix, 'dob')}
              type="date"
              disabled={disableCustomerEdit}
              value={getValue(values, namePrefix, 'dob')}
              onChange={e => {
                setFieldValue(getInputName(namePrefix, 'dob'), e.target.value);
              }}
            />
          </div>
        )}
      </div>
      {disableCustomerEdit && (
        <Typography variant="caption" style={{ color: 'var(--darkGrey)' }}>
          This customer&apos;s information cannot be edited
        </Typography>
      )}
    </div>
  );
}
